import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, Collapse, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { ExpandLessOutlined, ExpandMoreOutlined } from '@material-ui/icons';
import clsx from 'clsx';
import { ReactNode, useState } from 'react';

export interface ExpandableInfoPanelProps {
  tooltipText?: string;
  children?: ReactNode;
  defaultExpanded?: boolean;
  moreInfoLabel?: string;
  className?: string;
  contentClassName?: string;
  qaAttribute?: string;
  moreInfoTestId?: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: '100%',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
    },
    leftSection: {
      display: 'flex',
      alignItems: 'center',
    },
    moreInfoToggle: {
      display: 'flex',
      alignItems: 'center',
      color: theme.palette.primary.main,
      userSelect: 'none',
    },
    moreInfoText: {
      fontSize: pxToRem(12),
      fontWeight: 500,
    },
    caretIcon: {
      fontSize: pxToRem(20),
    },
    content: {
      padding: theme.spacing(1, 0),
      fontSize: pxToRem(12),
      lineHeight: 1.5,
      color: theme.palette.text.primary,
      marginTop: theme.spacing(0.5),
    },
  }),
);

export default function ExpandableInfoPanel({
  tooltipText,
  children,
  defaultExpanded = false,
  moreInfoLabel = 'More Info',
  className,
  contentClassName,
  qaAttribute,
  moreInfoTestId,
}: ExpandableInfoPanelProps): JSX.Element | null {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(defaultExpanded);

  if (!tooltipText) {
    return <>{children}</>;
  }

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Box className={clsx(classes.container, className)} data-testid={qaAttribute}>
      <Box className={classes.header} onClick={handleToggle}>
        <Box className={classes.leftSection}>{children}</Box>
        <Box className={classes.moreInfoToggle} data-testid={moreInfoTestId}>
          <Typography className={classes.moreInfoText} color="primary">
            {moreInfoLabel}
          </Typography>
          {expanded ? (
            <ExpandLessOutlined className={classes.caretIcon} color="primary" />
          ) : (
            <ExpandMoreOutlined className={classes.caretIcon} color="primary" />
          )}
        </Box>
      </Box>
      <Collapse in={expanded}>
        <Box className={clsx(classes.content, contentClassName)}>
          <Typography variant="body2">{tooltipText}</Typography>
        </Box>
      </Collapse>
    </Box>
  );
}
